/**
 * MASTERY — THE COST OF THE PLAYER'S ONE VERB, AS A NUMBER.
 *
 * CONTRACT.md section 6, property 3: "THE CHOICE MUST COST SOMETHING. If
 * targeting a rare fish is strictly better, everyone targets the best thing and
 * it is a button rather than a decision. Chasing a rarer species must trade
 * against something real — PER-SPECIES MASTERY: hours spent targeting A are
 * hours not spent building mastery in B."
 *
 * Before this file that sentence had no referent. `SimState` carried `catches`,
 * `xp`, `skill` and `targetSpecies`; the only occurrences of the word "mastery"
 * in the tree were forward-compatibility fixtures for a field that did not
 * exist, and `src/render/chart-control.ts` said so in a docstring rather than
 * showing the player a cost that was not being charged. Surfaces round 1
 * shipped the control, so the choice existed and was FREE.
 *
 * ---------------------------------------------------------------------------
 * WHAT A MASTERY POINT IS. DERIVED, not chosen.
 * ---------------------------------------------------------------------------
 *
 * ONE POINT IS ONE TICK OF A CAST THAT LANDED THAT SPECIES. One tick is one
 * second (TICK_MS in time.ts), so mastery is READ IN HOURS BY DIVIDING BY 3,600
 * and there is no conversion constant to get wrong. The rate is not a number
 * somebody liked; it is the identity, and everything below follows from it.
 *
 * FOUR CONSEQUENCES, each a fact about the engine rather than a preference:
 *
 *  1. IT IS INVARIANT UNDER SKILL, which is the property that makes "hours" the
 *     honest word. `castTicksFor` halves a cast at skill 100, so a skilled
 *     player finishes twice as many casts in an hour and each one credits half
 *     as much. The product is the hour. If mastery were credited PER LANDING
 *     instead, skill 100 would master a species twice as fast in wall-clock
 *     time, and skill would quietly become a mastery rate lever — the one field
 *     entitlements.ts exists to keep unpurchasable. mastery.test.ts measures the
 *     invariance rather than asserting it.
 *
 *  2. IT CANNOT BE RECONSTRUCTED FROM `catches`, so it is genuinely a new field
 *     and not the landed count wearing a different name. A cast's length is
 *     `castTicksFor(bestUnlockedSpecies(level), skill)` — a function of the
 *     player's LEVEL, never of the quarry — so a hundred minnows landed at
 *     level 1 cost 400 ticks and a hundred minnows landed at level 99 cost
 *     2,400. Mastery is path-dependent; the catch count is not. That is the
 *     measured answer to "why not just count fish", and it is why
 *     chart-control.ts was right to refuse to dress one up as the other.
 *
 *  3. JUNK AND SPECIALS CREDIT NOTHING, because neither lands a species. An
 *     hour of fishing therefore yields slightly less than an hour of mastery —
 *     94.7% of it at level 99, where the pace is moonfish at 20/1000 junk and
 *     33/1000 special. Hours spent with nothing to show for them is what junk
 *     already is; this is the same hour, counted the same way.
 *
 *  4. TOTAL MASTERY IS THE SAME WHATEVER YOU TARGET. Landings and cast lengths
 *     are identical across targets — that is property 2, asserted to the byte in
 *     targeting.test.ts — so the choice moves mastery between species and never
 *     creates or destroys it. THAT IS THE COST, stated exactly: the hours are
 *     spent either way, and the only question the verb answers is which
 *     species they land in.
 *
 * ---------------------------------------------------------------------------
 * IT IS CREDITED TO THE SPECIES LANDED, NEVER TO THE SPECIES TARGETED.
 * ---------------------------------------------------------------------------
 *
 * OWNER RULING, CONTRACT.md section 1.5j(d), 2026-09-02: "BYCATCH COUNTS TOWARD
 * THE SPECIES LANDED. You caught a mackerel, your mackerel mastery ticks."
 *
 * Bycatch is NOT built in this round. This field does not assume its absence and
 * does not need editing when it lands: `stepTick` credits `quarry.id`, the same
 * expression that decides which key the catch map increments, so whatever a
 * future cast lands is what it masters. mastery.test.ts asserts the two keys
 * agree on every landing over a long run rather than trusting the comment, which
 * is the form of the assertion that survives bycatch being added by somebody
 * else.
 *
 * ---------------------------------------------------------------------------
 * WHAT MASTERY DOES, AND WHAT IT STILL MAY NOT DO.
 * ---------------------------------------------------------------------------
 *
 * THIS SECTION USED TO SAY THAT NOTHING READ MASTERY AT ALL, and it was true
 * for exactly one round. It closed with the sentence "this round makes the cost
 * real and leaves the reward unbuilt rather than inventing a curve for it", and
 * named the reward as the owner's to rule on. The owner had already ruled:
 * CONTRACT.md section 1.5l(a), MASTERY DEEPENS THE SPECIES IT IS SPENT ON.
 * `src/sim/variants.ts` is that reward, and this paragraph is rewritten rather
 * than left standing, because a stale sentence about a firewall reads as a
 * guarantee and is worse than no sentence.
 *
 * NO TEMPO READS MASTERY, AND THAT HALF IS STILL FORCED. `stepTick` decides the
 * cast length, the outcome roll, the xp and the coins WITHOUT looking at it, and
 * there is no expression in which they could. Property 2 requires a targeted and
 * an untargeted run to land the same catch budget with ZERO tolerance; mastery
 * accumulates only under a target, so any TEMPO that read it would break that
 * equality on the first tick it applied. The bar has the reward this forbids and
 * it is worth naming: Melvor's fishing mastery 65 removes junk for that fish
 * (gauntlet/refs/bar-depth.json, MEASURED from the wiki's raw wikitext), and junk
 * is a throughput axis here.
 *
 * THE COMPOSITION READS IT, ONCE. `stepTick` asks `variantFor` which form of the
 * quarry came up, and a form scales that one cast's weight draw. That moves
 * `weightG`, `records` and the `variants` tally and nothing else — the same three
 * quantities targeting itself is allowed to move, for the same reason. MEASURED
 * (node tools/lanes/progression/variants.mjs): with all three rungs open the
 * aggregate weight effect is about 2%, and the visible reward is that a mastered
 * mackerel can weigh 1,400 g where the species band stops at 700.
 *
 * THE CONSEQUENCE FOR THE GUESS BELOW, STATED HERE BECAUSE IT CHANGED. Moving
 * MASTERY_FULL used to move no earned quantity at all. It now moves when a
 * player's fish start getting bigger, because every rung of the variant ladder
 * is an exact multiple of it and nothing in that file carries a threshold of its
 * own. That is deliberate — one number to rule on — and it is a raising of the
 * stakes on a ruling the owner still owes.
 */

import { MAX_COUNT } from './state';

/**
 * THE ONE CONSTANT THAT IS A GUESS, ISOLATED SO THE OWNER CAN RULE ON ONE LINE.
 *
 * Everything above is derived. This is not: it is the number of mastery points
 * at which a species is called MASTERED, and nothing in the run's measured bars
 * determines it. Melvor's mastery 99 is a level on an XP curve whose shape has
 * nothing to do with this one, and reading a threshold off it would be borrowing
 * a number's authority without its meaning.
 *
 * GUESS: 72,000 points = 20 HOURS of landing casts on one species.
 *
 * The reasoning is stated so the owner can reject the reasoning and not just the
 * number. Level 99 on the idle baseline is 742 hours (CONTRACT.md section 1.5d,
 * MEASURED and re-measured by pacing.test.ts every run). There are 24 species.
 *
 * WHAT THAT GUESS BUYS, MEASURED at this value by running the shipped engine
 * (`node tools/lanes/progression/pacing.mjs`, 2026-09-02):
 *   - 21.0 hours of FISHING to master one species at level 99. Not 20: junk and
 *     specials land nothing and credit nothing, so one hour in twenty is spent
 *     with nothing to show for it.
 *   - 504 hours to master all 24, against 742 hours to reach level 99. It fits
 *     inside the climb with room left over, which is the property being aimed
 *     for: a deliberate player can finish the collection without the last
 *     species becoming a second game.
 *   - A player who NEVER touches the chart control still masters 8 species by
 *     742 hours, because the default target is the best species they have
 *     unlocked and their mastery rolls up the ladder behind them. A player who
 *     retargets each time a species fills masters 23. THE VERB IS WORTH FIFTEEN
 *     SPECIES over a full playthrough, and that is the clearest single number
 *     for what the choice is actually worth.
 *
 * Ten hours would make the whole collection a third of the climb and the choice
 * cheap; forty would put it past 99. Both alternatives are one edit here.
 *
 * IT IS ONE CONSTANT AND THE WHOLE REWARD LADDER HANGS OFF IT. This paragraph
 * used to read "nothing derives a gameplay effect from it", which stopped being
 * true when CONTRACT.md section 1.5l(a) was built: every threshold in
 * src/sim/variants.ts is MASTERY_FULL divided by three, times one, or times
 * three, so moving this number moves the whole ladder in proportion and moves no
 * rung relative to another. Nothing derives a THROUGHPUT effect from it, which is
 * the part that is still forced. tools/lanes/progression/pacing.mjs prints what it
 * costs and tools/lanes/progression/variants.mjs prints what it pays, both at
 * whatever this is set to, so the owner rules on a number they have seen the
 * consequences of on both sides.
 */
export const MASTERY_FULL = 72_000;

/** Ticks in an hour, at TICK_MS = 1000. Written here so a report never guesses. */
export const MASTERY_TICKS_PER_HOUR = 3_600;

/**
 * The gameplay ceiling on one species' mastery, and it is MAX_COUNT for the same
 * reason the catch counts are: it is an accumulator, and every accumulator in
 * this state stops rising before it can leave the exactly-representable integer
 * range. 10^12 points is 31,700 years of landing casts on one fish.
 *
 * A CEILING IS NOT A NERF and this one is not a loader bound. See FIELD_CLASS in
 * state.ts: `mastery` is EARNED, so a loader bounds it by MAX_EARNED and by
 * nothing narrower, exactly as it does `catches`.
 */
export const MAX_MASTERY = MAX_COUNT;

/** Whole hours of landing casts a mastery total represents. */
export function masteryHoursOf(points: number): number {
  if (!Number.isFinite(points) || points <= 0) return 0;
  return points / MASTERY_TICKS_PER_HOUR;
}

/** True once a species has been fished for MASTERY_FULL ticks of landing casts. */
export function isMastered(points: number): boolean {
  return Number.isFinite(points) && points >= MASTERY_FULL;
}

/**
 * ONE ENTRY OF A PER-SPECIES TALLY, SET, WITH THE KEYS IN SORTED ORDER.
 *
 * SIM-D2, and the reason this is one function rather than two. `SimState` now
 * carries two maps keyed by species id — `catches` and `mastery` — and both are
 * subject to the same rule: the loader rebuilds a map in its own order and the
 * simulator spreads it, so a map whose key order records WHEN a species was
 * first seen makes (load . save) the identity on every value and not on the
 * object. An honest ten-minute run came back deep-unequal that way once already.
 *
 * A second copy of this rule is a second place it can be got wrong, which is
 * CONTRACT.md section 10 doctrine 11 applied to a function instead of a number.
 * `stepTick` calls this for both maps and `roundtrip.test.ts` proves the fixed
 * point on raw bytes for both.
 *
 * The sort runs at most once per species per playthrough: every later write
 * takes the spread, because a spread preserves the position of a key it
 * overwrites.
 */
export function withTallyEntry(
  tally: Readonly<Record<string, number>>,
  id: string,
  value: number,
): Record<string, number> {
  if ((tally[id] ?? 0) > 0) return { ...tally, [id]: value };
  const out: Record<string, number> = {};
  for (const key of [...Object.keys(tally), id].sort()) {
    out[key] = key === id ? value : (tally[key] ?? 0);
  }
  return out;
}
